import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle2, 
  User, 
  Building2, 
  Calendar,
  ArrowRight,
  Sparkles,
  X
} from "lucide-react";
import { toast } from "sonner";

const CreatorOnboarding = ({ onClose, onComplete }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchOnboardingStatus = async () => {
      try {
        const response = await axiosInstance.get("/creator/onboarding-status");
        if (active) setStatus(response.data);
      } catch (error) {
        console.error("Error fetching onboarding status:", error);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchOnboardingStatus();
    return () => { active = false; };
  }, []);

  const handleCompleteOnboarding = async () => {
    try {
      await axiosInstance.post("/creator/complete-onboarding");
      toast.success("Welcome aboard! You're all set to start creating events.");
      if (onComplete) onComplete();
      if (onClose) onClose();
    } catch (error) {
      toast.error("Failed to complete onboarding");
    }
  };

  const steps = [
    {
      id: "profile_complete",
      title: "Complete Your Profile",
      description: "Your basic profile is set up",
      icon: User,
      action: null, // Already done via Google auth
      completed: status?.steps?.profile_complete
    },
    {
      id: "bank_linked",
      title: "Link Bank Account",
      description: "Connect your bank to receive payouts",
      icon: Building2,
      action: () => navigate("/creator/settings"),
      actionLabel: "Link Bank",
      completed: status?.steps?.bank_linked
    },
    {
      id: "first_event_created",
      title: "Create Your First Event",
      description: "Schedule your first live show",
      icon: Calendar,
      action: () => navigate("/creator/create-event"),
      actionLabel: "Create Event",
      completed: status?.steps?.first_event_created
    }
  ];

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-3">
        <div className="rounded-full border border-white/10 bg-gray-900/95 px-4 py-2 text-sm text-white shadow-xl">
          Loading setup…
        </div>
      </div>
    );
  }

  // If onboarding is complete, don't show anything
  if (status?.is_complete && status?.steps?.onboarding_completed) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-3"
      data-testid="creator-onboarding"
    >
      <div
        className="max-h-[calc(100dvh-1.5rem)] w-full max-w-md overflow-y-auto overscroll-contain rounded-2xl border border-white/10 bg-gray-900 shadow-2xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="creator-onboarding-panel"
      >
        {/* Header */}
        <div
          className="relative bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3.5"
          data-testid="creator-onboarding-header"
        >
          <button
            onClick={onClose}
            className="absolute right-2.5 top-2.5 flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
            aria-label="Close creator setup"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 pr-9">
            <Sparkles className="h-5 w-5 shrink-0 text-yellow-300" />
            <h2 className="text-lg font-extrabold text-white sm:text-xl">Welcome, Creator!</h2>
          </div>
          <p className="mt-1 pr-9 text-xs leading-4 text-white/80 sm:text-sm">
            Let's get you set up to start earning on ShowMeLive
          </p>
        </div>

        {/* Progress */}
        <div className="p-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-xs font-semibold text-gray-400">Your progress</span>
            <span className="text-xs font-extrabold text-white sm:text-sm">
              {status?.current_step || 0} of {status?.total_steps || 3} completed
            </span>
          </div>

          {/* Progress bar */}
          <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-gray-800">
            <div 
              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
              style={{ width: `${((status?.current_step || 0) / (status?.total_steps || 3)) * 100}%` }}
            />
          </div>

          {/* Steps */}
          <div className="space-y-2">
            {steps.map((step) => (
              <div 
                key={step.id}
                className={`flex min-h-[4.25rem] items-center gap-2.5 rounded-xl border p-3 transition-all ${
                  step.completed 
                    ? "border-green-600/30 bg-green-900/20"
                    : "border-gray-700 bg-gray-800/50"
                }`}
                data-testid={`onboarding-step-${step.id}`}
              >
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                  step.completed 
                    ? "bg-green-600" 
                    : "bg-gray-700"
                }`}>
                  {step.completed ? (
                    <CheckCircle2 className="h-5 w-5 text-white" />
                  ) : (
                    <step.icon className="h-4 w-4 text-gray-400" />
                  )}
                </div>
                
                <div className="min-w-0 flex-1">
                  <h3 className={`text-sm font-bold leading-4 ${step.completed ? "text-green-400" : "text-white"}`}>
                    {step.title}
                  </h3>
                  <p className="mt-0.5 text-xs leading-4 text-gray-400">{step.description}</p>
                </div>

                {!step.completed && step.action && (
                  <Button
                    onClick={step.action}
                    size="sm"
                    className="h-9 shrink-0 rounded-lg bg-blue-600 px-3 text-xs font-bold hover:bg-blue-700"
                  >
                    {step.actionLabel}
                    <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Complete Button */}
          {status?.is_complete && !status?.steps?.onboarding_completed && (
            <Button
              onClick={handleCompleteOnboarding}
              className="mt-4 h-11 w-full bg-gradient-to-r from-blue-600 to-purple-600 font-bold text-white hover:from-blue-700 hover:to-purple-700"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Complete Setup & Start Creating!
            </Button>
          )}

          {/* Skip for now */}
          <button
            onClick={onClose}
            className="mt-3 min-h-9 w-full text-xs font-medium text-gray-500 transition hover:text-gray-300"
          >
            I'll finish this later
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatorOnboarding;
